import { inject, Injectable } from '@angular/core';
import { createLogger } from '@hunting-nature/shared-core/logging';
import { NavController } from '@ionic/angular';
import { Store } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, filter, map, of, switchMap, tap, withLatestFrom } from 'rxjs';

import { DeviceApiService, isRegistrationError } from '../../../shared/services/device-api.service';
import { DeviceRegistrationFlowService } from '../../../shared/services/device-registration-flow.service';
import { RegistrationError } from '../../../shared/services/device-registration-error';
import { CannesOwnershipService } from '../../../shared/services/cannes-ownership.service';
import { deriveHardwareId } from '../../../shared/utils/derive-hardware-id.util';
import { PermissionValues } from '@hunting-nature/shared-core/types/permission.type';

import { DeviceRegistrationActions } from './device-registration.actions';
import { DeviceRegistrationSelectors } from './device-registration.reducer';
import { HuntingProductsSelectors } from './hunting-products.reducer';

@Injectable()
export class DeviceRegistrationEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly deviceApiService = inject(DeviceApiService);
  private readonly deviceRegistrationFlowService = inject(DeviceRegistrationFlowService);
  private readonly cannesOwnershipService = inject(CannesOwnershipService);
  private readonly log = createLogger('DeviceRegistrationEffects');

  selectDeviceForRegistration$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DeviceRegistrationActions.selectDeviceForRegistration),
      switchMap(({ deviceId, serialNumber, modelName }) => {
        this.log.debug('Initiating assignment for device', { deviceId, serialNumber, modelName });

        return this.deviceApiService.initiateAssignment$(serialNumber, modelName).pipe(
          map(challenge => {
            this.log.debug('Challenge request succeeded', { challengeId: challenge.challengeId });

            return DeviceRegistrationActions.requestChallengeSucceeded({
              deviceId,
              challenge,
            });
          }),
          catchError((error: unknown) => {
            const registrationError = isRegistrationError(error) ? error : RegistrationError.NETWORK_ERROR;
            this.log.error('Challenge request failed', { error, registrationError });

            return of(DeviceRegistrationActions.requestChallengeFailed({ error: registrationError }));
          })
        );
      })
    )
  );

  collectDeviceProof$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DeviceRegistrationActions.requestChallengeSucceeded),
      switchMap(({ deviceId, challenge }) => {
        this.log.debug('Starting BLE proof collection for device', { deviceId });

        return this.deviceRegistrationFlowService.collectDeviceProof$(deviceId, challenge).pipe(
          map(({ challengeId, deviceSignature }) =>
            DeviceRegistrationActions.collectProofSucceeded({
              deviceId,
              challengeId,
              deviceSignature,
            })
          ),
          catchError((error: unknown) => {
            this.log.error('BLE proof collection failed', { error });
            return of(DeviceRegistrationActions.collectProofFailed({ error: RegistrationError.NETWORK_ERROR }));
          })
        );
      })
    )
  );

  completeRegistration$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DeviceRegistrationActions.collectProofSucceeded),
      withLatestFrom(this.store.select(HuntingProductsSelectors.getPairedProducts)),
      switchMap(([{ deviceId, challengeId, deviceSignature }, pairedProducts]) => {
        this.log.debug('Starting proof submission for device', { deviceId });

        const product = pairedProducts.find(p => p.deviceId === deviceId);
        if (!product) {
          this.log.error('No product found for device', { deviceId });
          return of(DeviceRegistrationActions.registrationFailed({ error: RegistrationError.NETWORK_ERROR }));
        }

        const selectedDevice = this.store.selectSignal(DeviceRegistrationSelectors.selectedDevice$)();
        const modelName = (selectedDevice as { name?: string } | undefined)?.name ?? product.modelName;
        const hardwareId = deriveHardwareId(modelName ?? null, product.serialNumber ?? null);
        if (!hardwareId) {
          this.log.error('Could not derive hardwareId for device', {
            deviceId,
            modelName,
            serialNumber: product.serialNumber,
          });
          return of(DeviceRegistrationActions.registrationFailed({ error: RegistrationError.NETWORK_ERROR }));
        }

        this.log.debug('Derived hardwareId', { hardwareId });

        return this.deviceRegistrationFlowService.completeRegistrationWithValidation$(deviceId, pairedProducts, hardwareId, challengeId, deviceSignature).pipe(
          map(result => {
            this.log.debug('Registration succeeded', { hardwareId: result.hardwareId });

            return DeviceRegistrationActions.registrationSucceeded({
              hardwareId: result.hardwareId,
            });
          }),
          catchError((error: unknown) => {
            const failure = error as { error?: unknown; retryAfterMs?: number } | undefined;
            const failureError = failure?.error;
            const registrationError = isRegistrationError(failureError) ? failureError : RegistrationError.NETWORK_ERROR;
            this.log.error('Proof submission failed', { error, registrationError });

            // A 409 here always means a different account owns the device — the same account
            // resubmitting returns 200. This reconciliation is a no-op safety net, not a required path.
            if (registrationError === RegistrationError.DEVICE_ALREADY_CLAIMED) {
              return this.cannesOwnershipService
                .resolveOwnership$(deviceId, pairedProducts)
                .pipe(
                  map(resolution =>
                    resolution?.permission === PermissionValues.OWNER
                      ? DeviceRegistrationActions.registrationSucceeded({ hardwareId })
                      : DeviceRegistrationActions.registrationFailed({ error: registrationError })
                  )
                );
            }

            return of(DeviceRegistrationActions.registrationFailed({ error: registrationError, retryAfterMs: failure?.retryAfterMs }));
          })
        );
      })
    )
  );

  handleRateLimit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DeviceRegistrationActions.registrationFailed),
      filter(({ error }) => error === RegistrationError.RATE_LIMITED),
      switchMap(({ retryAfterMs }) =>
        this.deviceRegistrationFlowService
          .startRateLimitBackoff$(retryAfterMs)
          .pipe(map(({ until }) => DeviceRegistrationActions.rateLimitBackoffSet({ until })))
      )
    )
  );

  resetOnNewAttempt$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DeviceRegistrationActions.openDeviceRegistrationFlow),
      tap(() => this.deviceRegistrationFlowService.clearRateLimitBackoff()),
      map(() => DeviceRegistrationActions.resetRegistrationState())
    )
  );
}
